'use client';

import { ReactNode, useState } from 'react';
import Input from '../Input';

export type CalculatorType = 'volts-dbu' | 'return-level' | 'gain-offset';

const DBU_REF_VOLTS = 0.7746; // 0 dBu = 0.7746 V RMS
const DBU_MINUS_DBV = 2.218; // dBu = dBV + 2.218

const voltsToDbu = (v: number) => 20 * Math.log10(v / DBU_REF_VOLTS);
const dbuToVolts = (dbu: number) => DBU_REF_VOLTS * Math.pow(10, dbu / 20);

const parse = (s: string): number | null => {
  if (s.trim() === '') return null;
  const n = Number(s.replace(',', '.'));
  return Number.isFinite(n) ? n : null;
};

const fmtDb = (n: number) => {
  const r = Math.round(n * 10) / 10;
  const s = (Math.abs(r) < 0.05 ? 0 : r).toFixed(1);
  return r > 0 ? `+${s}` : s;
};

const fmtVolts = (v: number) => {
  if (v >= 100) return v.toFixed(0);
  if (v >= 10) return v.toFixed(1);
  if (v >= 1) return v.toFixed(2);
  return v.toFixed(3);
};

const Field = ({
  label,
  value,
  onChange,
  placeholder,
  unit,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  unit?: string;
}) => (
  <div className="flex flex-1 flex-col gap-1 min-w-[140px]">
    <span className="text-zinc-400 text-sm">{label}</span>
    <div className="flex items-center gap-2">
      <Input
        type="number"
        inputMode="decimal"
        step="any"
        value={value}
        placeholder={placeholder}
        onChange={onChange}
      />
      {unit && <span className="text-zinc-400 text-sm whitespace-nowrap">{unit}</span>}
    </div>
  </div>
);

const Result = ({
  label,
  value,
  hint,
}: {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
}) => (
  <div className="flex flex-col gap-1 border-t border-zinc-700 pt-4 mt-4">
    <span className="text-zinc-400 text-sm">{label}</span>
    <span className="text-2xl font-bold text-white">{value}</span>
    {hint && <span className="text-sm text-zinc-400">{hint}</span>}
  </div>
);

const Card = ({ title, children }: { title: string; children: ReactNode }) => (
  <div className="bg-zinc-800 rounded-lg border border-zinc-700 p-4 sm:p-6 flex flex-col gap-4">
    <h4 className="text-white font-bold">{title}</h4>
    {children}
  </div>
);

const Pending = <span className="text-zinc-500">—</span>;

const VoltsDbu = () => {
  const [volts, setVolts] = useState('');
  const [dbu, setDbu] = useState('');
  const [source, setSource] = useState<'volts' | 'dbu'>('volts');

  const onVolts = (s: string) => {
    setSource('volts');
    setVolts(s);
    const v = parse(s);
    setDbu(v != null && v > 0 ? (Math.round(voltsToDbu(v) * 10) / 10).toString() : '');
  };
  const onDbu = (s: string) => {
    setSource('dbu');
    setDbu(s);
    const d = parse(s);
    setVolts(d != null ? fmtVolts(dbuToVolts(d)) : '');
  };

  // Compute from whichever box the reader typed in so the result echoes their
  // number exactly instead of a value that round-tripped through rounding.
  const v = parse(volts);
  const d = parse(dbu);
  const exactDbu = source === 'volts' ? (v != null && v > 0 ? voltsToDbu(v) : null) : d;
  const exactVolts = source === 'volts' ? (v != null && v > 0 ? v : null) : d != null ? dbuToVolts(d) : null;

  return (
    <Card title="Volts ↔ dBu">
      <p className="text-sm text-zinc-400">
        Type in either box. Volts are RMS, as read on a multimeter&apos;s AC setting.
      </p>
      <div className="flex flex-col sm:flex-row gap-4">
        <Field label="Multimeter reading" value={volts} onChange={onVolts} placeholder="6.40" unit="V RMS" />
        <Field label="Level" value={dbu} onChange={onDbu} placeholder="18.3" unit="dBu" />
      </div>
      <Result
        label="Result"
        value={
          exactDbu != null && exactVolts != null
            ? `${fmtVolts(exactVolts)} V RMS = ${fmtDb(exactDbu)} dBu`
            : Pending
        }
        hint={exactDbu != null ? `${fmtDb(exactDbu - DBU_MINUS_DBV)} dBV` : 'dBu = 20 × log10(V ÷ 0.775)'}
      />
    </Card>
  );
};

const ReturnLevel = () => {
  const [send, setSend] = useState('');
  const [lowered, setLowered] = useState('');
  const [peak, setPeak] = useState('');

  const s = parse(send);
  const l = parse(lowered);
  const p = parse(peak);
  const result = s != null && l != null && p != null ? s - Math.abs(l) - p : null;

  return (
    <Card title="Return Level">
      <p className="text-sm text-zinc-400">
        Plug the measured send straight into the return input, lower the tone until the input
        stops clipping, and enter what you see.
      </p>
      <div className="flex flex-col sm:flex-row gap-4">
        <Field label="Send level" value={send} onChange={setSend} placeholder="18.3" unit="dBu" />
        <Field label="Tone lowered by" value={lowered} onChange={setLowered} placeholder="6.5" unit="dB" />
        <Field label="Input peak reading" value={peak} onChange={setPeak} placeholder="-0.5" unit="dBFS" />
      </div>
      <Result
        label="Return level"
        value={result != null ? `${fmtDb(result)} dBu` : Pending}
        hint={
          result != null
            ? `${fmtVolts(dbuToVolts(result))} V RMS reaches 0 dBFS on this input`
            : 'Return = send − amount lowered − peak reading'
        }
      />
    </Card>
  );
};

const GainOffset = () => {
  const [spec, setSpec] = useState('');
  const [gain, setGain] = useState('');

  const s = parse(spec);
  const g = parse(gain);
  const result = s != null && g != null ? s - g : null;

  return (
    <Card title="Calibration Value with Added Gain">
      <p className="text-sm text-zinc-400">
        Every dB of interface gain lowers your clipping level by a dB.
      </p>
      <div className="flex flex-col sm:flex-row gap-4">
        <Field label="Interface max input level" value={spec} onChange={setSpec} placeholder="12" unit="dBu" />
        <Field label="Gain you added" value={gain} onChange={setGain} placeholder="6" unit="dB" />
      </div>
      <Result
        label="Enter in the plugin"
        value={result != null ? `${fmtDb(result)} dBu` : Pending}
        hint={result == null ? 'Value = max input level − gain added' : undefined}
      />
    </Card>
  );
};

export default function CalibrationCalculator({ type }: { type?: CalculatorType | string }) {
  switch (type) {
    case 'volts-dbu':
      return <VoltsDbu />;
    case 'return-level':
      return <ReturnLevel />;
    case 'gain-offset':
      return <GainOffset />;
    default:
      return <div className="text-zinc-400 text-sm italic">No calculator type selected</div>;
  }
}
